import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Cable, Sparkles } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

export default function SfpPanel({ hqModel, value, onChange, useCase, users, sites }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    api.get("/sfp/modules").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!useCase) return;
    api.get(`/sfp/suggest`, { params: { use_case: useCase, users: Number(users) || 1, sites: Number(sites) || 1 } })
      .then((r) => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [useCase, users, sites]);

  const suggestionMap = useMemo(() => {
    const m = {};
    for (const s of suggestions) m[s.sku] = s;
    return m;
  }, [suggestions]);

  const slots = data?.slots_by_model?.[hqModel] || { sfp: 0, sfp_plus: 0 };
  const totalSlots = slots.sfp + slots.sfp_plus;
  const totalSelected = useMemo(
    () => Object.values(value || {}).reduce((s, q) => s + (Number(q) || 0), 0),
    [value]
  );
  const totalCost = useMemo(() => {
    if (!data) return 0;
    return data.modules.reduce((sum, m) => sum + (Number(value?.[m.sku]) || 0) * m.price, 0);
  }, [data, value]);

  const setQty = useCallback((sku, qty) => {
    const next = { ...(value || {}) };
    const q = Math.max(0, Math.min(100, Number(qty) || 0));
    if (q === 0) delete next[sku]; else next[sku] = q;
    onChange(next);
  }, [value, onChange]);

  const applySuggestions = () => {
    if (!suggestions.length) return;
    const next = { ...(value || {}) };
    for (const s of suggestions) next[s.sku] = s.quantity;
    onChange(next);
    setOpen(true);
    toast.success(`${suggestions.length} suggestion(s) appliquée(s)`);
  };

  if (!data) return null;

  const hasSuggestions = suggestions.length > 0;

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="sfp-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-zinc-50"
        data-testid="sfp-panel-toggle"
      >
        <div className="flex items-center gap-2.5">
          <Cable className="h-4 w-4 text-[#D42B1E]" />
          <span className="text-sm font-semibold tracking-tight text-zinc-900">Connectique SFP / Datacenter</span>
          <span className="text-xs font-mono text-zinc-500">
            {hqModel} : {slots.sfp} × SFP · {slots.sfp_plus} × SFP+ ({totalSlots} ports optiques)
          </span>
          {hasSuggestions && (
            <span className="text-[10px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded-sm border bg-green-50 border-green-200 text-green-800" data-testid="sfp-suggestions-available">
              {suggestions.length} recommandation{suggestions.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {totalSelected > 0 && (
            <span className="text-xs font-mono text-[#D42B1E]" data-testid="sfp-summary">
              {totalSelected} module{totalSelected > 1 ? "s" : ""} · {fmt(totalCost)}
            </span>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-100 p-5">
          {hasSuggestions && (
            <div className="mb-4 flex items-start justify-between gap-3 border border-green-200 bg-green-50/60 rounded-sm p-3" data-testid="sfp-suggestion-bar">
              <div className="flex items-start gap-2 text-sm">
                <Sparkles className="h-4 w-4 text-green-700 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-900">Suggestion automatique pour ce déploiement</p>
                  <p className="text-xs text-green-800 mt-0.5">
                    {suggestions.map((s) => `${s.quantity}× ${s.sku}`).join(" · ")}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={applySuggestions}
                className="bg-green-700 hover:bg-green-800 text-white rounded-sm shrink-0"
                data-testid="sfp-apply-suggestions-button"
              >
                Appliquer
              </Button>
            </div>
          )}
          {totalSlots === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm mb-4">
              Le modèle <span className="font-mono">{hqModel}</span> n'a pas de port SFP. Sélectionner un modèle supérieur pour bénéficier de la connectique optique datacenter.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.modules.map((m) => {
              const incompatible =
                (m.slot === "sfp" && slots.sfp === 0) ||
                (m.slot === "sfp_plus" && slots.sfp_plus === 0);
              const sug = suggestionMap[m.sku];
              return (
                <div
                  key={m.sku}
                  className={`border rounded-sm p-3 relative ${incompatible ? "border-zinc-100 bg-zinc-50/50 opacity-60" : sug ? "border-green-300 bg-green-50/30" : "border-zinc-200"}`}
                  data-testid={`sfp-module-${m.sku}`}
                >
                  {sug && (
                    <span className="absolute -top-2 left-3 text-[10px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded-sm bg-green-700 text-white" data-testid={`sfp-recommended-${m.sku}`}>
                      Recommandé
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900">{m.name}</p>
                      <p className="mt-0.5 text-xs text-zinc-500 leading-relaxed">{m.description}</p>
                      {sug && (
                        <p className="mt-1.5 text-xs text-green-800 italic">→ {sug.reason}</p>
                      )}
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <span className="font-mono text-zinc-500">{m.sku}</span>
                        <span className={`font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm border text-[10px] ${m.slot === "sfp_plus" ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-zinc-100 border-zinc-300 text-zinc-700"}`}>
                          {m.slot === "sfp_plus" ? "SFP+ 10G" : "SFP 1G"}
                        </span>
                        <span className="font-mono text-zinc-900 font-semibold">{fmt(m.price)}</span>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Qté</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={value?.[m.sku] || ""}
                        onChange={(e) => setQty(m.sku, e.target.value)}
                        disabled={incompatible}
                        className="mt-1 w-20 rounded-sm h-9 text-center"
                        data-testid={`sfp-qty-${m.sku}`}
                        placeholder={sug ? String(sug.quantity) : "0"}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
